import React, { useState, useCallback, useContext } from "react";
import { Dialog } from "components/Dialog";
import { List, ListItem, ListItemText } from "@mui/material";
import { ChartListItem } from "interfaces/chart";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import { useFetchDeleteV2 } from "hooks/fetch/useFetchDeleteV2";
import { ConfirmationDialog } from "components/ConfirmationDialog";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { ChartListResponse } from "hooks/charts/useGetChartsList";

export interface LoadChartDialogProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  savedCharts?: ChartListItem[];
  onLoad: (chartId: number) => void;
  onDeleteChart: () => Promise<ChartListResponse>;
}

export const LoadChartDialog = ({
  open,
  onClose,
  savedCharts,
  loading,
  onLoad,
  onDeleteChart
}: LoadChartDialogProps): JSX.Element => {
  const [selectedChartId, setSelectedChartId] = useState<number | undefined>(
    undefined
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [chartToDelete, setChartToDelete] = useState<
    ChartListItem | undefined
  >();

  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const {
    loading: deleteLoading,
    deleteCallback: deleteChart
  } = useFetchDeleteV2(`charts/${chartToDelete?.id}`);

  const handleSubmit = useCallback(() => {
    if (selectedChartId !== undefined) {
      onLoad(selectedChartId);
    }
  }, [onLoad, selectedChartId]);

  const handleError = useCallback(
    (message: string) => {
      addSnackbarToQueue({
        message: message,
        severity: "error"
      });
    },
    [addSnackbarToQueue]
  );

  const handleDeleteCompleted = useCallback(async () => {
    if (chartToDelete?.id === selectedChartId) {
      setSelectedChartId(undefined);
    }

    setChartToDelete(undefined);
    addSnackbarToQueue({
      message: `"${chartToDelete?.chart_name}" deleted.`,
      severity: "success"
    });

    await onDeleteChart();
  }, [addSnackbarToQueue, chartToDelete, onDeleteChart, selectedChartId]);

  const handleDeleteChart = useCallback(() => {
    deleteChart().then(
      _fulfil => handleDeleteCompleted(),
      _reject => handleError(`Failed to delete "${chartToDelete?.chart_name}".`)
    );

    setDeleteDialogOpen(false);
  }, [chartToDelete, deleteChart, handleDeleteCompleted, handleError]);

  // Custom  compare function to check chart name
  const caseInsensitiveCompare = (
    a: ChartListItem,
    b: ChartListItem
  ): number => {
    return a.chart_name.localeCompare(b.chart_name, undefined, {
      sensitivity: "base"
    });
  };

  return (
    <Dialog
      dialogId="dialog-load-chart"
      open={open}
      onClose={onClose}
      title="Load Chart"
      confirmText="Load Chart"
      size="medium"
      actionsDisabled={loading}
      cancelDisabled={deleteLoading}
      onConfirm={handleSubmit}
      confirmDisabled={!selectedChartId || deleteLoading}
    >
      <ChartList>
        {savedCharts && savedCharts?.length > 0 ? (
          savedCharts.sort(caseInsensitiveCompare).map(chart => (
            <ListItem
              key={chart.id}
              button
              disabled={loading || deleteLoading}
              onClick={() => setSelectedChartId(chart.id)}
              selected={selectedChartId === chart.id}
            >
              <ListItemText primary={`${chart.chart_name}`} />
              <ListItemSecondaryAction>
                <IconButton
                  onClick={() => {
                    setDeleteDialogOpen(true);
                    setChartToDelete(chart);
                  }}
                  edge="end"
                  aria-label="delete"
                  disabled={loading || deleteLoading}
                  size="large"
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))
        ) : (
          <div>There are no saved charts.</div>
        )}
      </ChartList>
      <ConfirmationDialog
        loading={loading}
        open={deleteDialogOpen}
        title={`Are you sure you want to delete "${chartToDelete?.chart_name}"?`}
        onClose={() => {
          setDeleteDialogOpen(false);
        }}
        onConfirm={handleDeleteChart}
      />
    </Dialog>
  );
};

const ChartList = styled(List)`
  max-height: 300px;
  overflow-y: auto;
`;
